import { v4 as uuidv4 } from 'uuid';
import { BoardData, Board, Task, Column, Tag, BoardSettings, Subtask, User, Comment, Attachment } from '../types';

const createSampleTags = (): Record<string, Tag> => ({
  'tag-1': {
    id: 'tag-1',
    name: 'Urgent',
    color: '#ef4444',
    createdAt: new Date().toISOString()
  },
  'tag-2': {
    id: 'tag-2',
    name: 'Feature',
    color: '#3b82f6',
    createdAt: new Date().toISOString()
  },
  'tag-3': {
    id: 'tag-3',
    name: 'Bug',
    color: '#f59e0b',
    createdAt: new Date().toISOString()
  },
  'tag-4': {
    id: 'tag-4',
    name: 'Documentation',
    color: '#10b981',
    createdAt: new Date().toISOString()
  }
});

const createSampleUsers = (): Record<string, User> => ({
  'user-1': {
    id: 'user-1',
    name: 'John Doe',
    email: 'john@example.com',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=john',
    createdAt: new Date().toISOString()
  },
  'user-2': {
    id: 'user-2',
    name: 'Jane Smith',
    email: 'jane@example.com',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=jane',
    createdAt: new Date().toISOString()
  },
  'user-3': {
    id: 'user-3',
    name: 'Bob Johnson',
    email: 'bob@example.com',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=bob',
    createdAt: new Date().toISOString()
  }
});

const createSampleTasks = (): Record<string, Task> => ({
  'task-1': {
    id: 'task-1',
    title: 'Complete TypeScript Kanban Board',
    content: 'Build a comprehensive Kanban board with advanced features using TypeScript and React',
    priority: 'high',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    columnId: 'todo',
    subtasks: [
      {
        id: 'subtask-1',
        title: 'Implement drag and drop',
        completed: true,
        createdAt: new Date().toISOString()
      },
      {
        id: 'subtask-2',
        title: 'Add TypeScript types',
        completed: true,
        createdAt: new Date().toISOString()
      },
      {
        id: 'subtask-3',
        title: 'Implement dark mode',
        completed: false,
        createdAt: new Date().toISOString()
      }
    ],
    tags: ['tag-1', 'tag-2'],
    dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    completed: false,
    assigneeIds: ['user-1'],
    comments: [],
    attachments: []
  },
  'task-2': {
    id: 'task-2',
    title: 'Write Documentation',
    content: 'Create comprehensive README and user guide for the Kanban board',
    priority: 'medium',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    columnId: 'inprogress',
    subtasks: [
      {
        id: 'subtask-4',
        title: 'Write installation guide',
        completed: true,
        createdAt: new Date().toISOString()
      },
      {
        id: 'subtask-5',
        title: 'Add feature descriptions',
        completed: false,
        createdAt: new Date().toISOString()
      }
    ],
    tags: ['tag-4'],
    completed: false,
    assigneeIds: ['user-2'],
    comments: [],
    attachments: []
  },
  'task-3': {
    id: 'task-3',
    title: 'Deploy to Production',
    content: 'Deploy the Kanban board to a production environment',
    priority: 'low',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    columnId: 'done',
    subtasks: [],
    tags: ['tag-3'],
    completed: true,
    completedAt: new Date().toISOString(),
    assigneeIds: ['user-3'],
    comments: [],
    attachments: []
  }
});

const createSampleColumns = (): Record<string, Column> => ({
  'todo': {
    id: 'todo',
    title: 'To Do',
    taskIds: ['task-1'],
    color: 'blue',
    createdAt: new Date().toISOString(),
    order: 0
  },
  'inprogress': {
    id: 'inprogress',
    title: 'In Progress',
    taskIds: ['task-2'],
    color: 'yellow',
    createdAt: new Date().toISOString(),
    order: 1
  },
  'done': {
    id: 'done',
    title: 'Done',
    taskIds: ['task-3'],
    color: 'green',
    createdAt: new Date().toISOString(),
    order: 2
  }
});

const createSampleBoard = (id: string, name: string): Board => ({
  id,
  name,
  description: `A ${name.toLowerCase()} board for organizing tasks and projects`,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  columns: createSampleColumns(),
  tasks: createSampleTasks(),
  columnOrder: ['todo', 'inprogress', 'done'],
  tags: createSampleTags(),
  users: createSampleUsers(),
  settings: {
    theme: 'light',
    showCompletedTasks: true,
    enableNotifications: true,
    defaultPriority: 'medium'
  }
});

export const initialData: BoardData = {
  boards: {
    'board-1': createSampleBoard('board-1', 'Work'),
    'board-2': {
      ...createSampleBoard('board-2', 'Personal'),
      tasks: {},
      columns: {
        'personal-todo': { ...createSampleColumns()['todo'], id: 'personal-todo', title: 'Personal Tasks' },
        'personal-done': { ...createSampleColumns()['done'], id: 'personal-done', title: 'Completed' }
      },
      columnOrder: ['personal-todo', 'personal-done']
    },
    'board-3': {
      ...createSampleBoard('board-3', 'Study'),
      tasks: {},
      columns: {
        'study-todo': { ...createSampleColumns()['todo'], id: 'study-todo', title: 'Study Tasks' },
        'study-review': { ...createSampleColumns()['inprogress'], id: 'study-review', title: 'Review' },
        'study-done': { ...createSampleColumns()['done'], id: 'study-done', title: 'Completed' }
      },
      columnOrder: ['study-todo', 'study-review', 'study-done']
    }
  },
  currentBoardId: 'board-1',
  actionHistory: []
};

export const saveData = (data: BoardData): void => {
  try {
    localStorage.setItem('kanbanData', JSON.stringify(data));
  } catch (error) {
    console.error('Error saving data to localStorage:', error);
  }
};

export const loadData = (): BoardData => {
  try {
    const saved = localStorage.getItem('kanbanData');
    return saved ? JSON.parse(saved) : initialData;
  } catch (error) {
    console.error('Error loading data from localStorage:', error);
    return initialData;
  }
};

export const exportBoardData = (data: BoardData): string => {
  return JSON.stringify(data, null, 2);
};

export const importBoardData = (jsonString: string): BoardData => {
  try {
    const importedData = JSON.parse(jsonString);

    // Validate the imported data structure
    if (!importedData.boards || !importedData.currentBoardId) {
      throw new Error('Invalid board data format');
    }

    return importedData;
  } catch (error) {
    console.error('Error importing board data:', error);
    throw error;
  }
};

export const createTask = (
  title: string,
  content: string = '',
  priority: 'low' | 'medium' | 'high' = 'medium',
  columnId: string = 'todo'
): Task => {
  return {
    id: uuidv4(),
    title,
    content,
    priority,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    columnId,
    subtasks: [],
    tags: [],
    completed: false,
    assigneeIds: [],
    comments: [],
    attachments: []
  };
};

export const updateTask = (task: Task, updates: Partial<Task>): Task => {
  return {
    ...task,
    ...updates,
    updatedAt: new Date().toISOString()
  };
};

export const createSubtask = (title: string): Subtask => {
  return {
    id: uuidv4(),
    title,
    completed: false,
    createdAt: new Date().toISOString()
  };
};

export const createTag = (name: string, color: string): Tag => {
  return {
    id: uuidv4(),
    name,
    color,
    createdAt: new Date().toISOString()
  };
};

export const createColumn = (title: string, color: string): Column => {
  return {
    id: uuidv4(),
    title,
    color,
    taskIds: [],
    createdAt: new Date().toISOString(),
    order: 0
  };
};

export const generateHeatmapData = (tasks: Record<string, Task>): any[] => {
  const completedTasks = Object.values(tasks).filter(task => task.completed && task.completedAt);

  // Group by date
  const dateGroups: Record<string, number> = {};
  completedTasks.forEach(task => {
    if (task.completedAt) {
      const date = new Date(task.completedAt).toISOString().split('T')[0];
      dateGroups[date] = (dateGroups[date] || 0) + 1;
    }
  });

  // Convert to heatmap format
  return Object.entries(dateGroups).map(([date, count]) => ({
    date,
    count,
    level: Math.min(count, 4) // Max level of 4
  }));
};
